import type { ApiKey, CreateApiKeyRequest, CreateApiKeyResponse, UpdateApiKeyRequest } from '@/stores/auth'
import { isDemoDeployment } from '@/utils/deployment'
import type { AxiosRequestConfig } from 'axios'
import JSONBig from 'json-bigint'
import { apiCall, apiCallWithBigInt } from './axiosInstance'
import { apiKeysApi as demoApiKeysApi } from './demo/apiKeysApi'

/** Callers pass the tenant explicitly on both scopes so the header can never drift from the tenant
 *  the caller resolved. Only org-level access impersonates admin — project-level access has to stay
 *  under the caller's own RBAC. */
const tenantScopedConfig = (tenantId?: string, impersonateAdmin = true): AxiosRequestConfig | undefined =>
	tenantId
		? {
				headers: {
					'x-bytebeam-tenant': tenantId,
					...(impersonateAdmin ? { 'x-bytebeam-impersonate': 'admin' } : {}),
				},
			}
		: undefined

const v3ApiKeysApi = {
	getApiKeys: async (tenantId?: string, impersonateAdmin = true): Promise<ApiKey[]> =>
		apiCallWithBigInt('/v3/apikeys', tenantScopedConfig(tenantId, impersonateAdmin)),
	createApiKey: async (
		request: CreateApiKeyRequest,
		tenantId?: string,
		impersonateAdmin = true,
	): Promise<CreateApiKeyResponse> => {
		const tenantConfig = tenantScopedConfig(tenantId, impersonateAdmin)
		return apiCall('/v3/apikeys', {
			method: 'POST',
			data: request,
			...tenantConfig,
			headers: {
				'Content-Type': 'application/json',
				...tenantConfig?.headers,
			},
			transformRequest: [
				data => {
					if (data && typeof data === 'object') {
						return JSONBig.stringify(data)
					}
					return data
				},
			],
		})
	},
	updateApiKey: async (
		id: number,
		request: UpdateApiKeyRequest,
		tenantId?: string,
		impersonateAdmin = true,
	): Promise<{ status: string }> =>
		apiCall(`/v3/apikeys/${id}`, {
			method: 'PATCH',
			data: request,
			...tenantScopedConfig(tenantId, impersonateAdmin),
		}),
	deleteApiKey: async (id: number, tenantId?: string, impersonateAdmin = true): Promise<{ status: string }> =>
		apiCall(`/v3/apikeys/${id}`, {
			method: 'DELETE',
			...tenantScopedConfig(tenantId, impersonateAdmin),
		}),
}

export const apiKeysApi = isDemoDeployment() ? demoApiKeysApi : v3ApiKeysApi
